import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import {
    type ColourType,
    EcoTagType,
    type EngineType,
    type ExtraType,
    type FilterEquipmentType,
    type FinanceOptionType,
    type PackType,
    type ServiceProductType,
    type UpholsteryType,
    type WheelType,
    type WinterWheelType,
} from "../../../common-deprecated/types/CommonTypes";
import type { CarMotorizationType } from "../types/CarType";
import type { BasketType } from "../types/BasketType";
import type { SavedConfigType } from "../types/SaveTypes";
import type { ItemsType } from "../../../common-deprecated/redux/typeHelpers";
import CarDataConsolidator from "./classes/CarDataConsolidator";
import { find, getCARDBPath } from "../../../common-deprecated/utils";
import type {
    ColourFromOtherGradesTexusType,
    ColourTexusType,
    ExteriorColourTexusType,
} from "../../../common-deprecated/types/TexusTypes";
import {
    get3dJsonObject,
    getCarImage,
    type ImageOptionsType,
} from "../../../common-deprecated/settings/utils/ccisSettingUtils";

/**
 * Attach modelToken/disableTokens/priceCalculationService query variables to the given url.
 */
export const addTokenDebugVariablesToUrl = (commonSettings: CommonSettingsType, url: string): string => {
    const { disableTokens, modelToken, priceCalculationService } = commonSettings.query;
    const items = [
        typeof disableTokens !== "undefined" && `disableTokens=${disableTokens.toString()}`,
        modelToken && `modelToken=${modelToken}`,
        priceCalculationService && `priceCalculationService=${priceCalculationService.toString()}`,
    ].filter(Boolean);

    return `${url}?${items.join("&")}`;
};

/**
 * Takes the engine type of the FetchMotorization call (engine step) and the engine type of the FetchFullConfig call (customize+)
 * Returns which ecoTag should be displayed
 */
export const getEcoTagType = (engine: EngineType | CarMotorizationType["engine"]): EcoTagType | undefined => {
    if (engine.fuelCell) return EcoTagType.FuelCell;
    if (engine.plugin) return EcoTagType.Plugin;
    if (engine.hybrid) return EcoTagType.Hybrid; // both fuelCell and plugin can be hybrids they should be checked first
    if (engine.electric) return EcoTagType.Electric;
    return undefined;
};

/**
 * Helper function to get an array of option/accessory ids. Will add childExtras and its parents depending on settings.
 * Casts shortIds to strings to have an easier flow setup (avoid an array with multiple return types).
 */
export const getExtraIds = (
    extraType: "option" | "accessory",
    arr: (ExtraType | WheelType | WinterWheelType)[],
    useShortIds: boolean,
    excludeStandard: boolean,
): string[] =>
    arr.reduce((opts: string[], extra) => {
        const pushUnique = (value: string): number => (!opts.includes(value) ? opts.push(value) : 0);
        if (extra.type === extraType && (!excludeStandard || (excludeStandard && !extra.standard))) {
            if (extra.isWheel) {
                pushUnique(extra[useShortIds ? "shortId" : "id"].toString());
            } else {
                const childOption = CarDataConsolidator.getSelectedExtraOption(extra as ExtraType);
                if (childOption) {
                    pushUnique(childOption[useShortIds ? "shortId" : "id"].toString());
                } else {
                    pushUnique(extra[useShortIds ? "shortId" : "id"].toString());
                }
            }
        }
        return opts;
    }, []);

export const getExternalConfigOptionsAccessoryIds = (
    basket: BasketType,
    items: ItemsType,
): { options: string[]; accessories: string[] } => {
    const { wheel } = basket;
    const combinedExtras: (ExtraType | WheelType | WinterWheelType)[] = [
        ...CarDataConsolidator.getAllRelevantBasketExtras(basket, items),
    ];

    combinedExtras.push(wheel);

    // Add the winterwheel
    if (basket.winterWheel) {
        combinedExtras.push(basket.winterWheel);
    }

    const data: { options: string[]; accessories: string[] } = { options: [], accessories: [] };

    if (combinedExtras.length > 0) {
        data.options = getExtraIds("option", combinedExtras, false, false);
        data.accessories = getExtraIds("accessory", combinedExtras, false, false);
    }

    return data;
};

/**
 * @deprecated Use the same function but without deprecated instead
 */
export const getExternalConfigOptionsAccessoryIdsDeprecated = (
    basket: BasketType,
    items: ItemsType,
    useShortIds: boolean = false,
    excludeStandard: boolean = false,
    // TODO Clean this up with service product refactor
    financeOption: FinanceOptionType | null = null,
): { options: string[]; accessories: string[] } => {
    const { wheel } = basket;
    const combinedExtras: (ExtraType | WheelType | WinterWheelType)[] = [
        ...CarDataConsolidator.getAllRelevantBasketExtras(basket, items),
    ];

    combinedExtras.push(wheel);

    // Add the winterwheel
    if (basket.winterWheel) {
        combinedExtras.push(basket.winterWheel);
    }

    const data: { options: string[]; accessories: string[] } = { options: [], accessories: [] };

    // Only if financeOption is monthly should the financePackExtras be added, currently only relevant for getExternalConfigurationObject
    // TODO Might need a refactor to also utilize getSelectedFinancePackEquipment here instead of relying on this only
    if (financeOption === "monthly") {
        combinedExtras.push(...basket.financePackExtras);
    }

    const standardFinancePacksExtras = basket.financePacks
        .reduce(
            (packEquipment, pack) => {
                return [...packEquipment, ...pack.equipment];
            },
            [] as (ExtraType | WheelType | ServiceProductType)[],
        )
        .filter((extra) => extra.standard);

    if (combinedExtras.length > 0) {
        data.options = getExtraIds("option", combinedExtras, useShortIds, excludeStandard).filter(
            (extraId) => !find(extraId, standardFinancePacksExtras),
        );
        data.accessories = getExtraIds("accessory", combinedExtras, useShortIds, excludeStandard).filter(
            (extraId) => !find(extraId, standardFinancePacksExtras),
        );
    }

    return data;
};
export const findPackExtraParent = (packExtraId: string, packs: PackType[]): PackType => {
    const parentPack = packs.reduce((acc, pack) => {
        if (pack && find(packExtraId, pack.equipment)) {
            return pack;
        }
        return acc;
    });

    return parentPack;
};

export const getColourSwatch = (
    settings: CommonSettingsType,
    colour: ExteriorColourTexusType | ColourTexusType | ColourFromOtherGradesTexusType,
): string => {
    const colourSwatch = colour.Assets
        ? colour.Assets.find((asset) => asset.Type.toLowerCase().replace(/\s*/g, "") === "colourchooser-fullsize")
        : "";
    return colourSwatch ? getCARDBPath(settings, colourSwatch.Url) : "";
};

export const getTyCodeFromState = (data: SavedConfigType | null): { code: string; link: string } | null => {
    if (!data) return null;
    return {
        code: data.Personal ? data.Personal.Code : data.ConfigurationCode,
        link: data.Personal ? data.Personal.Link : data.ConfigurationLink,
    };
};
export const getCarImageFromConfig = (
    settings: CommonSettingsType,
    mode: string,
    frame: number | string,
    modelId: string,
    carId: string,
    imageOptions: ImageOptionsType = {},
    colour?: ColourType,
    upholstery?: UpholsteryType,
    wheel: WheelType | WinterWheelType | null = null,
    packs: PackType[] = [],
    extras: ExtraType[] = [],
    useColourUpholsteryPlaceholder: boolean = false,
    winterWheel?: WinterWheelType | null,
    is3DJson?: boolean,
    filterItems: FilterEquipmentType[] = [],
): string => {
    let colourCodes;
    if (useColourUpholsteryPlaceholder) {
        colourCodes = "_{COLOUR}_{UPHOLSTERY}";
    } else {
        colourCodes = colour
            ? `_${escape(colour.internalCode || colour.code)}${upholstery ? `_${escape(upholstery.internalCode)}` : ""}`
            : "";
    }

    const packsUri = packs.length > 0 ? `/packs/${packs.map((pack) => pack.shortId).join()}` : "";

    let wheelParts: (WheelType | ExtraType)[] = wheel && !wheel.standard && wheel.objectType === "wheel" ? [wheel] : [];

    if (!!winterWheel && winterWheel.setItems) {
        // @ts-expect-error - See comment above about TS not liking merging multiple type arrays.
        wheelParts = [winterWheel, ...winterWheel.setItems.filter((x) => x.visible)];
    }

    const getExtraShortIds = (extraType: "option" | "accessory"): string[] => {
        return (
            extras
                .filter((extra) => extra.type === extraType)
                .filter(
                    (extra) =>
                        (!extra.standard && !extra.selectedOptionId) ||
                        extra.parent ||
                        extra.isUpholstery ||
                        extra.isPackExtra,
                )
                // @ts-expect-error - TS doesn't like merging complex arrays :( https://github.com/Microsoft/TypeScript/issues/26378
                .concat(wheelParts.filter((extra: any) => extra.type === extraType))
                .map((extra) => extra.shortId)
        );
    };

    const getSetItemsShortIds = (childType: "option" | "accessory"): string[] =>
        extras
            .filter((extra) => extra.setItems && extra.setItems.length > 0)
            .reduce((acc: string[], extra) => {
                const s = extra.setItems
                    .map((setItem) => (setItem.type === childType && setItem.showsOnCar ? setItem.shortId : ""))
                    .filter((shortId) => !!shortId);
                return s ? acc.concat(s) : acc;
            }, []);

    const getChildrenShortIds = (childType: "option" | "accessory"): string[] =>
        extras
            .filter((extra) => extra.type === childType)
            .reduce((childOpts: string[], extra) => {
                const childOption = CarDataConsolidator.getSelectedExtraOption(extra);
                if (childOption) childOpts.push(childOption.shortId);
                return childOpts;
            }, []);

    const getFilterItemsShortIds = (type: "option" | "accessory"): string[] =>
        filterItems.filter((item) => item.type === type).map((item) => `${item.shortId}`);

    const accessoryExtras = Array.from(
        new Set(
            getExtraShortIds("accessory")
                .concat(getChildrenShortIds("accessory"))
                .concat(getSetItemsShortIds("accessory"))
                .concat(getFilterItemsShortIds("accessory")),
        ),
    ).join(",");

    const optionExtras = Array.from(
        new Set(
            getExtraShortIds("option")
                .concat(getChildrenShortIds("option"))
                .concat(getSetItemsShortIds("option").concat(getFilterItemsShortIds("option"))),
        ),
    ).join(",");

    const optionsUri = optionExtras.length > 0 ? `/options/${optionExtras}` : "";
    const accessoriesUri = accessoryExtras.length > 0 ? `/accessories/${accessoryExtras}` : "";

    const configUri = `${packsUri}${optionsUri}${accessoriesUri}`;

    return is3DJson
        ? get3dJsonObject(settings as CommonSettingsType, modelId, carId, imageOptions, configUri, colourCodes)
        : getCarImage(settings, mode, frame, modelId, carId, imageOptions, configUri, colourCodes);
};
